// 牌（TTile）型別與工具函式
// 對應 PRD §13.1 TTile、§8.1 自動理牌排序規則
//
// 設計原則：
// - TTile 是 readonly 純資料，所有操作都產生新物件，不變動原物件
// - id 全局唯一（0–143），用來區分「同種但不同實體」的牌
//   （例：兩張 1 萬 id 不同，這樣才能分辨「牌山裡哪一張被抓走」）
// - tileKey() 取得「同種牌 key」（不含 id），用於比對是否同種
//   例：兩張 1 萬 tileKey 都是 'man-1'，但 id 不同

import type { TSuit } from './types';
import { Suit } from '@core/types';

/**
 * 一張牌的不可變資料結構。
 *
 * - `id`：全局唯一序號 0–143（一副 144 張）
 * - `suit`：花色分類
 * - `rank`：點數（萬筒條 1–9；風 1–4 對應東南西北；元 1–3 對應中發白；花 1–8 對應春夏秋冬梅蘭竹菊）
 */
export type TTile = {
  readonly id: number;
  readonly suit: TSuit;
  readonly rank: number;
};

/**
 * 各 suit 的 rank 上限。
 * 萬筒條 1–9、風 1–4、元 1–3、花 1–8。
 */
const SUIT_RANK_MAX = {
  man: 9,
  pin: 9,
  sou: 9,
  wind: 4,
  dragon: 3,
  flower: 8,
} as const satisfies Record<TSuit, number>;

/**
 * 各 suit 每個 rank 的張數。
 * 花牌每種只有 1 張（共 8 張），其餘每種 4 張。
 */
const SUIT_COPIES = {
  man: 4,
  pin: 4,
  sou: 4,
  wind: 4,
  dragon: 4,
  flower: 1,
} as const satisfies Record<TSuit, number>;

/**
 * 排序權重，數字小者排前面。
 * 對應 PRD §8.1 自動理牌規則：萬→筒→條→風→元→花。
 */
const SUIT_ORDER: Record<TSuit, number> = {
  man: 0,
  pin: 1,
  sou: 2,
  wind: 3,
  dragon: 4,
  flower: 5,
};

/**
 * 各 suit 的索引基底：把每種牌對應到單一整數 0..41。
 * 0-8 man1-9 / 9-17 pin1-9 / 18-26 sou1-9 / 27-30 wind / 31-33 dragon / 34-41 flower。
 * hu / tenpai 用 0..33 子集（花不參與結構）；tile-sprite frame 對映用完整 0..41。
 */
const SUIT_INDEX_BASE = {
  man: 0,
  pin: 9,
  sou: 18,
  wind: 27,
  dragon: 31,
  flower: 34,
} as const satisfies Record<TSuit, number>;

/** 單張牌 → 0..41 索引（萬筒條 0..26、風 27..30、元 31..33、花 34..41）。 */
export const tileToIndex = (t: TTile): number => SUIT_INDEX_BASE[t.suit] + (t.rank - 1);

/** tileToIndex 的反查表：index → { suit, rank }（與 SUIT_RANK_MAX 排序一致，單一真相來源）。 */
export const TILE_INDEX_TO_SUIT_RANK: ReadonlyArray<{ suit: TSuit; rank: number }> = (() => {
  const arr: { suit: TSuit; rank: number }[] = [];
  for (const suit of Object.keys(SUIT_RANK_MAX) as TSuit[]) {
    for (let rank = 1; rank <= SUIT_RANK_MAX[suit]; rank++) arr.push({ suit, rank });
  }
  return arr;
})();

/**
 * 產生完整的 144 張牌組（每張 id 從 0 開始遞增、唯一）。
 *
 * 計算驗證：
 *   萬 9×4 + 筒 9×4 + 條 9×4 = 108
 *   風 4×4 = 16
 *   元 3×4 = 12
 *   花 8×1 = 8
 *   合計 = 144
 *
 * 順序固定（萬→筒→條→風→元→花，每種內 rank 1→max），
 * 洗牌前的初始狀態，方便 unit test 斷言。
 */
export const createFullTileSet = (): TTile[] => {
  const tiles: TTile[] = [];
  let id = 0;
  for (const suit of Object.keys(SUIT_RANK_MAX) as TSuit[]) {
    const max = SUIT_RANK_MAX[suit];
    const copies = SUIT_COPIES[suit];
    for (let rank = 1; rank <= max; rank++) {
      for (let c = 0; c < copies; c++) {
        tiles.push({ id: id++, suit, rank });
      }
    }
  }
  return tiles;
};

/** 是否花牌（春夏秋冬梅蘭竹菊）。 */
export const isFlower = (t: TTile): boolean => t.suit === Suit.Flower;

/** 是否字牌（風或三元，俗稱「番牌」）。 */
export const isHonor = (t: TTile): boolean => t.suit === 'wind' || t.suit === Suit.Dragon;

/** 是否數牌（萬筒條）。順子只能由數牌組成。 */
export const isNumeric = (t: TTile): boolean =>
  t.suit === Suit.Man || t.suit === Suit.Pin || t.suit === Suit.Sou;

/**
 * 比較兩張牌的排序順序（搭配 Array.sort 用）。
 * 回傳 < 0：a 排前面；> 0：b 排前面；= 0：同種牌。
 *
 * 規則：先比 suit（萬→筒→條→風→元→花），再比 rank。
 */
export const compareTiles = (a: TTile, b: TTile): number => {
  const suitDiff = SUIT_ORDER[a.suit] - SUIT_ORDER[b.suit];
  if (suitDiff !== 0) return suitDiff;
  return a.rank - b.rank;
};

/**
 * 取得「同種牌 key」字串（不含 id）。
 *
 * 用途：判斷兩張牌「是不是同一種」，例如手牌中找對子、刻子。
 * 範例：1 萬 → 'man-1'；東風 → 'wind-1'；中 → 'dragon-1'。
 */
export const tileKey = (t: TTile): string => `${t.suit}-${t.rank}`;

/** 兩張牌是否同種（同 suit + rank，不論 id）。 */
export const isSameKind = (a: TTile, b: TTile): boolean =>
  a.suit === b.suit && a.rank === b.rank;

/**
 * 排序一組牌（不變動原陣列）。
 * 對應 PRD §8.1 自動理牌：每次手牌變動後呼叫即可保持有序。
 */
export const sortTiles = (tiles: readonly TTile[]): TTile[] => [...tiles].sort(compareTiles);
